import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

/**
 * Interceptor global de errores de las requests al backend Laravel:
 *  - Loguea todo fallo (status + método + URL) para diagnóstico (queda en consola
 *    aunque el usuario no tenga Network abierto).
 *  - 401: dispara `auth.handlePossibleSessionExpiry()` (re-check /auth/me → banner
 *    de reingreso si la sesión murió). Se excluyen /auth/me y /sanctum/csrf-cookie
 *    para no hacer loop (son los propios chequeos de auth).
 *  - 419 (CSRF vencido, solo escrituras): refresca la cookie CSRF (self-healing).
 *
 * Re-lanza el error: el manejo existente de cada facade sigue funcionando. El
 * banner es aditivo.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isLaravelRequest(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        console.warn(`[http] ${error.status} ${req.method} ${req.url}`);

        if (error.status === 401 && !isAuthProbe(req.url)) {
          void auth.handlePossibleSessionExpiry();
        } else if (error.status === 419) {
          void auth.ensureCsrfCookie(true);
        }
      }

      return throwError(() => error);
    }),
  );
};

function isLaravelRequest(url: string): boolean {
  return url.startsWith(environment.apiUrl) || url.startsWith(`${environment.backendUrl}/sanctum`);
}

/** /auth/me y /sanctum/csrf-cookie son los propios chequeos de sesión: no re-disparar. */
function isAuthProbe(url: string): boolean {
  return url.includes('/auth/me') || url.includes('/sanctum/csrf-cookie');
}
